import type {
  InputProps
} from '@chakra-ui/react'
import {
  FormErrorMessage,
  forwardRef,
  Image,
  Input,
  InputGroup,
  InputLeftElement
} from '@chakra-ui/react'
import { customAddDays } from 'BrokerTsLib/src/extensions/date.extensions'
import type { FieldError } from 'BrokerTsLib/src/rhf'
import { useController, useFormContext } from 'BrokerTsLib/src/rhf'
import { Czech } from 'flatpickr/dist/l10n/cs'
import type { BaseOptions } from 'flatpickr/dist/types/options'
import moment from 'moment'
import type { ChangeEventHandler } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import Flatpickr from 'react-flatpickr'

import { formatDate } from '@/utils/formatDate'

import calendarIcon from '../../images/calendar-icon.svg'

interface SimpleDatepickerProps extends InputProps {
  date?: Date
  onDateChange: (date?: Date) => void
  isOpen?: boolean
  options?: Partial<BaseOptions>
  onChange?: ChangeEventHandler<HTMLInputElement> | undefined
  errors?: FieldError | undefined
  onClose?: Function
}

type DatepickerProps = {
  isOpen?: boolean
  options?: Partial<BaseOptions>
  handleDateChange?: (date: Date | undefined) => void
} & Pick<InputProps, 'isDisabled' | 'placeholder' | 'autoComplete' | 'htmlSize'>



export const ReactHookFormDatepicker = ({ options, isOpen, handleDateChange, ...inputProps }: DatepickerProps & { name: string }) => {
  const { control } = useFormContext()
  const { name } = inputProps

  const {
    field: { ref, onChange, onBlur },
    fieldState: { error }
  } = useController({
    name, control
  })

  const { watch } = useFormContext()

  const onDateChange = (date: Date | undefined) => {
    onChange(date ?? '')
  }

  return (
    <BaseDatepicker
      ref={ref}
      date={watch(name)}
      onDateChange={handleDateChange ?? onDateChange}
      onBlur={onBlur}
      errors={error}
      options={options}
      isOpen={isOpen}
      {...inputProps}
    />
  )
}

// TODO: stejny uz s carkulkou. Spojit do brokerTsLib
export const BaseDatepicker = forwardRef((props: SimpleDatepickerProps, ref) => {
  const { options, date: originalDate, isOpen = false, onClose, errors, isDisabled } = props
  const { minDate, disable, } = options || {}
  const fp = useRef<Flatpickr>(null)

  // useMemo kvuli zmene data v ramci formulare, napr. nastaveni data 1 registrace z VinOnline
  const oDate = useMemo(() => !originalDate || originalDate as any == '' ? undefined : new Date(originalDate), [originalDate])
  const [date, setDate] = useState(oDate)

  useEffect(() => {
    setDate(oDate)
  }, [oDate])

  const handleDateChange = (date?: Date) => {
    const d = date && moment(date).isValid() ? new Date(date) : undefined

    props.onDateChange(d)
    setDate(d)
  }

  useEffect(() => {
    if (isOpen) {
      fp?.current?.flatpickr?.open()
      fp?.current?.flatpickr?.input?.focus()
    }
    else
      fp?.current?.flatpickr?.close()
    return () => { }
  }, [isOpen])

  const errorMessage = errors && errors?.message

  useEffect(() => {
    if (minDate) {
      if (moment(date ?? new Date()).isBefore(moment(minDate))) {
        const from = customAddDays(date ?? new Date(), 1)
        const fromSelectedDateToMinDay = {
          from: formatDate(from),
          to: formatDate(customAddDays(minDate as Date, -1)),
        }

        const disableFromSelectedToMin = [fromSelectedDateToMinDay]

        // If selected date is before minDate, set minDate to selected date
        fp.current?.flatpickr.set('minDate', date ?? from)
        // Disable all dates after selected date and before minDate
        fp.current?.flatpickr.set('disable', [...(disable ?? []), ...disableFromSelectedToMin])
        // Redraw flatpickr
        fp.current?.flatpickr.redraw()
      } else {
        fp.current?.flatpickr.set('minDate', minDate)
        fp.current?.flatpickr.set('disable', [{
          to: formatDate(customAddDays(minDate as Date, -1)),
        }
        ])
      }
    }

    return () => { }
  }, [minDate, date, disable])

  return (
    <>
      <Flatpickr
        ref={fp}
        onBlur={(e) => {
          if (!fp.current?.flatpickr.isOpen) {
            const inputVal = fp.current?.flatpickr.input.value
            handleDateChange(!inputVal || !moment(inputVal, 'DD.MM.YYYY').isValid() ? undefined : moment(inputVal, 'DD.MM.YYYY').toDate())
          }

          // onBlur?.(e)
        }}
        options={{
          ...options,
          minDate: undefined,
          // allowInvalidPreload: true, // pokud je true, tak to pada na invalid date (napr kdyz se zada string)
          locale: Czech,
          animate: true,
          dateFormat: 'd.m.Y',
          allowInput: true,
          onClose: () => onClose?.(),
        }}
        value={date}
        render={({ defaultValue, onBlur }, ref) => {
          return (
            <InputGroup>
              <InputLeftElement
                pointerEvents="none"
                children={
                  <Image src={calendarIcon} width={4} height={4} alt="" />
                }
              />

              <Input
                defaultValue={defaultValue}
                ref={ref}
                htmlSize={props.htmlSize ?? 8}
                isDisabled={isDisabled}
                onBlur={(e) => {
                  onBlur?.(e)
                }}
              />

            </InputGroup>
          )
        }}
        onChange={([date]) => handleDateChange(date)}
      />

      {errorMessage && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
    </>
  )
})